import { terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  BillingCapacityForecastSchema,
  BillingGlobalSettingsSchema,
  BillingNotificationConfigSchema,
  BillingOverviewResponseSchema,
  BillingPortfolioResponseSchema,
  BillingSettingsSchema,
  ListBillingNotificationStateResponseSchema,
  ListModelBillingRatesResponseSchema,
  ModelBillingRateSchema,
  SendBillingTestAlertResponseSchema,
  UpsertModelBillingRateBodySchema,
} from './billing.schemas';
import { COCKPIT_LICENSE_WRITE_PERMISSIONS } from './cockpit-permissions';

export const cockpitBillingRoutes = {
  listModelBillingRates: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/billing/model-rates',
      summary: 'List internal per-model billing rates and markup overrides',
      inputStructure: 'detailed',
    })
    .output(ListModelBillingRatesResponseSchema),
  upsertModelBillingRate: terraContract
    .meta({ permissions: [...COCKPIT_LICENSE_WRITE_PERMISSIONS] })
    .route({
      method: 'PUT',
      path: '/billing/model-rates/{modelName}',
      summary: 'Set the internal rate and markup override for a model',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ modelName: z.string() }),
        body: UpsertModelBillingRateBodySchema,
      }),
    )
    .output(ModelBillingRateSchema),
  getBillingPortfolio: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/billing/portfolio',
      summary:
        'Cross-tenant AI spend, budgets, and blended margin roll-up across all licensed tenants',
      inputStructure: 'detailed',
    })
    .output(BillingPortfolioResponseSchema),
  getBillingGlobalSettings: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/billing/global-settings',
      summary:
        'Get Terra-wide global billing defaults (default customer markup)',
      inputStructure: 'detailed',
    })
    .output(BillingGlobalSettingsSchema),
  upsertBillingGlobalSettings: terraContract
    .meta({ permissions: [...COCKPIT_LICENSE_WRITE_PERMISSIONS] })
    .route({
      method: 'PUT',
      path: '/billing/global-settings',
      summary:
        'Update Terra-wide global billing defaults applied when a tenant has no markup',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: BillingGlobalSettingsSchema }))
    .output(BillingGlobalSettingsSchema),
  getBillingOverview: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/billing/{tenantId}',
      summary:
        'AI capacity by tier, baseline progress, and per-model margin for a tenant',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ tenantId: z.string() }) }))
    .output(BillingOverviewResponseSchema),
  getBillingForecast: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/billing/{tenantId}/forecast',
      summary:
        'Forecast AI capacity consumption through the end of the contract term from onboarded targets and recent scan cost',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ tenantId: z.string() }) }))
    .output(BillingCapacityForecastSchema),
  getBillingNotificationConfig: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/billing/{tenantId}/notification-config',
      summary: 'Get budget-threshold notification config for a tenant',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ tenantId: z.string() }) }))
    .output(BillingNotificationConfigSchema),
  upsertBillingNotificationConfig: terraContract
    .meta({ permissions: [...COCKPIT_LICENSE_WRITE_PERMISSIONS] })
    .route({
      method: 'PUT',
      path: '/billing/{tenantId}/notification-config',
      summary: 'Update budget-threshold notification config for a tenant',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ tenantId: z.string() }),
        body: BillingNotificationConfigSchema,
      }),
    )
    .output(BillingNotificationConfigSchema),
  listBillingNotificationState: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/billing/{tenantId}/notification-state',
      summary:
        'List budget thresholds already notified for a tenant with the last notification time',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ tenantId: z.string() }) }))
    .output(ListBillingNotificationStateResponseSchema),
  sendBillingTestAlert: terraContract
    .meta({ permissions: [...COCKPIT_LICENSE_WRITE_PERMISSIONS] })
    .route({
      method: 'POST',
      path: '/billing/{tenantId}/notification-test',
      summary:
        'Send a test budget alert with current AI capacity usage to the configured recipients',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ tenantId: z.string() }) }))
    .output(SendBillingTestAlertResponseSchema),
  getBillingSettings: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/billing/{tenantId}/settings',
      summary: 'Get per-tier AI capacity budgets, baseline budgets, and markup',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ tenantId: z.string() }) }))
    .output(BillingSettingsSchema),
  upsertBillingSettings: terraContract
    .meta({ permissions: [...COCKPIT_LICENSE_WRITE_PERMISSIONS] })
    .route({
      method: 'PUT',
      path: '/billing/{tenantId}/settings',
      summary:
        'Update per-tier AI capacity budgets, baseline budgets, and customer price markup',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ tenantId: z.string() }),
        body: BillingSettingsSchema,
      }),
    )
    .output(BillingSettingsSchema),
} as const;
